import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import startCase from 'lodash/startCase';
import sortBy from 'lodash/sortBy';
import Avatar from '@mui/material/Avatar';
import { getCurrentOffice } from '@redux/selectors/OfficeSelector';
import { getUserName } from '@redux/selectors/UserSelector';
import { setCurrentOffice } from '@redux/modules/office';
import { loadFormDefinition } from '@redux/modules/formGeneration';

import useRowLock from '@hooks/useRowLock';
import useAcl from '@hooks/useAcl';

import Button from '@components/smartAdminUI/Button';
import Panel from '@components/smartAdminUI/Panel';
import ViewLoader from '@components/loaders/ViewLoader';

import StatusBadges from '@components/badges/StatusBadges';

const officeAttributesWhiteList = [
  'OfficeAssociationComments',
  'OfficeEmail',
  'OfficeFax',
  'OfficeMailAddress',
  'OfficeMailCity',
  'OfficeMailPostalCode',
  'OfficePhone',
  'OfficePostalCodePlus4',
  'OtherWebsite',
  'SocialMediaWebsiteUrlOrId',
  'TerminationDate',
  'TollFree',
  'WebFacebook',
  'WebTwitter',
];

const operationType = 'edit';
const definitionType = 'office';

const ViewOffices = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const currOffice = useSelector(getCurrentOffice);
  const username = useSelector(getUserName);

  const [editButtonClicked, setEditButtonClicked] = useState(false);

  const [urlParam, setUrlParam] = useQueryParam('UniqueID', StringParam); // unique ID of current listing from CLF

  const memoizedOfficeEntries = useMemo(() => (currOffice ? Object.entries(currOffice) : []), [currOffice]);
  const rowLockEntity = useMemo(() => ({ type: 'office', id: currOffice?.UniqueID }), [currOffice]);

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    lockedByMeWithinSession,
    impersonatedBy,
    isAdmin,
    lastUpdatedOn,
    lastCheckedOn,
    checkStatus,
    createLock,
    discardChanges,
    LockMessage,
  } = useRowLock({ rowLockEntity });

  const onEditOffice = () => {
    if (currOffice) {
      dispatch(loadFormDefinition(operationType, definitionType, null, currOffice?.UniqueID, 'officeACL'));
    }
  };

  const {
    getOfficeAcl,
    canEditForm,
    isAllComplete,
  } = useAcl();
  const isAllAclComplete = isAllComplete();

  useEffect(() => {
    if (urlParam) {
      dispatch(setCurrentOffice(urlParam));
    }
  }, []);

  /*
    This has to be done using location. Currently, when the user loads a form, it redirects the user to the
      create/edit page. When this happens, it clears out the path and search values in the location object.
      We can't memo the location and use that as a comparison, because technically the locations will be
      the same on the reload, so we can't reset the search value in the location object.
  */
  useEffect(() => {
    if (location?.search === '') {
      if (currOffice) {
        setUrlParam(currOffice?.UniqueID, 'replaceIn');
      }
    }
  }, [location]);

  useEffect(() => {
    if (currOffice?.UniqueID) {
      // retrieves EDIT and COPY permissions for offices
      getOfficeAcl({
        action: 'view',
        formname: 'Office',
        uniqueId: currOffice.UniqueID,
      });

      // retrieves form permissions for offices
      getOfficeAcl({
        action: 'forms',
        formname: 'Office',
        uniqueId: currOffice.UniqueID,
      });

      if (location?.search === '' || location?.search !== currOffice?.UniqueID) {
        setUrlParam(currOffice?.UniqueID, 'replaceIn');
      }
    }
  }, [currOffice]);

  const onClickEdit = () => {
    setEditButtonClicked(true);
    createLock(onEditOffice);
  };

  if (currOffice) {
    return (
      <>
        <div className="subheader">
          <h1 className="subheader-title">
            <i className="subheader-icon fal fa-briefcase mr-2" />
            View
            <span className="fw-300 ml-2 mr-2">Office</span>
          </h1>
        </div>
        {!isAllAclComplete && !canEditForm ? (
          <ViewLoader type={definitionType} />
        ) : (
          <>
            {((lockedByMeWithinSession && !editButtonClicked) || (locked && !lockingAuthorized && !editButtonClicked)) && (
              <LockMessage
                rowLockEntity={rowLockEntity}
                lockedBy={lockedBy}
                lockedByMeWithinSession={lockedByMeWithinSession}
                impersonatedBy={impersonatedBy}
                userName={username}
                isAdmin={isAdmin}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onDiscard={discardChanges}
                displayActions={canEditForm}
              />
            )}
            <div className={locked && !lockingAuthorized ? 'row disabled' : 'row'}>
              <div className="col-lg-4 d-flex">
                <Panel customClass="w-100">
                  <div className="d-flex flex-column align-items-center justify-content-center p-4">
                    <Avatar
                      sx={{
                        minHeight: '250px',
                        maxHeight: '250px',
                        width: '100%',
                      }}
                      variant="rounded"
                      alt={currOffice.OfficeName}
                      src="/broken-image.jpg"
                    >
                      <i className="fas fa-building fa-6x" />
                    </Avatar>
                  </div>
                  <h3 className="mb-0 text-center">{currOffice.OfficeName}</h3>
                  <p className="mt-3 mb-3 text-center">
                    <StatusBadges status={currOffice.OfficeStatus} />
                    <span className="ml-2">{currOffice.OfficeMlsId}</span>
                  </p>
                  <p className="mb-4 text-center mt-2">
                    <a href={`phone: ${currOffice.OfficePhone} `}>
                      <i className="far fa-phone-rotary mr-2" />
                      {currOffice.OfficePhone}
                    </a>
                  </p>
                  <p className="mb-4 text-center mt-4 mb-2">
                    <a href={`https://maps.google.com/?q=${currOffice.OfficeAddress1}+${currOffice.OfficeCity}+${currOffice.OfficePostalCode} `} target="_blank" rel="noreferrer">
                      <i className="far fa-location mr-2" />
                      {currOffice.OfficeAddress1}
                      <br />
                      {`${startCase(currOffice.OfficeCity)}, Alberta `}
                      <br />
                      {currOffice.OfficePostalCode}
                    </a>
                  </p>
                </Panel>
              </div>
              <div className="col-lg-8 d-flex">
                <Panel
                  customClass="w-100"
                  header="Office Details"
                  panelToolbar={(
                    canEditForm && (
                      <Button
                        name="btnEditOffice"
                        variant="primary"
                        onClick={onClickEdit}
                        disabled={locked && !lockingAuthorized}
                      >
                        <i className="far fa-edit mr-2" />
                        Edit
                      </Button>
                    )
                  )}
                >
                  <table className="table borderless">
                    <tbody>
                      {sortBy(memoizedOfficeEntries).map(([key, value]) => {
                        if (!officeAttributesWhiteList.includes(key)) return null;
                        if (value) {
                          return (
                            <tr key={key}>
                              <th className="w-50">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}</th>
                              <td>{value}</td>
                            </tr>
                          );
                        }
                        return null;
                      })}
                      {memoizedOfficeEntries.length === 0 && 'No Values to Show'}
                    </tbody>
                  </table>
                </Panel>
              </div>
            </div>
          </>
        )}
      </>
    );
  }
  return null;
};

export default ViewOffices;
